import { useEffect } from 'react';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDestructive = false,
}: ConfirmDialogProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onCancel}
      />

      {/* Dialog */}
      <div className={`relative ${tokens.card.bg} rounded-landing ${tokens.card.shadow} ${tokens.border.light} border max-w-sm w-full p-4 z-10`}>
        <h2 className={`text-lg font-bold ${tokens.text.primary} mb-3 font-serif`}>{title}</h2>
        <p className={`text-sm ${tokens.text.muted} mb-4`}>{message}</p>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className={`flex-1 ${tokens.button.secondary} py-2.5 px-4 text-sm`}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 py-2.5 px-4 text-sm ${
              isDestructive
                ? 'bg-red-600 hover:bg-red-700 text-white rounded-landing font-semibold transition-all active:scale-95 min-h-[48px]'
                : tokens.button.primary
            }`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
